import { useFeedback } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { saveTicket, SaveTicketValues, TicketData } from "../..";
import { Async } from "../../../../_shared";
import { combineMetaWithConfig } from "../../_shared/hooks/useTicketMeta";
import { SaveTicketValuesWithFullMeta } from "../types/SaveTicketValues";

type SaveTicketValuesActions = {
  beforeSave?: (
    values: SaveTicketValuesWithFullMeta
  ) => Async<SaveTicketValuesWithFullMeta>;
  onSaveSuccess?: (
    response: TicketData,
    values: SaveTicketValuesWithFullMeta
  ) => Async<void>;
  onSaveError?: (error: any) => Async<void>;
};

export const useSaveTicketForm = (actions?: SaveTicketValuesActions) => {
  const t = useTranslation();
  const { handleLoadingFeedback } = useFeedback();

  async function onSave(values: SaveTicketValues) {
    // set default title for ticket
    if (!values.title && !values.description) {
      values.title = "maintenance:ticket.create.database.default.subject";
    }

    // If the title is not set, use the first 10 words of the description
    // as the title. This is a workaround for the fact that the title is not
    // always set in the form, and we want to avoid sending an empty title
    // to the server. A title is needed by the database schema.
    if (!values.title) {
      let newtitle = values.description.split(" ").slice(0, 10).join(" ");

      //Check if the title is longer that 255 characters
      if (newtitle.length > 255) newtitle = newtitle.slice(0, 255);

      values.title = newtitle;
    }

    // handle message data
    if (!values.existingTicketId && !values.message) {
      values.message = {
        title: "maintenance:ticket.create.database.subject.1",
        description: values.description,
      };
    }

    if (values.message)
      values.message.data = {
        translation: { ticketName: values.title },
        type: "userinput",
      };

    const valuesWithMeta: SaveTicketValuesWithFullMeta = {
      ...values,
      meta: {
        fields: await combineMetaWithConfig(values.meta),
      },
    };

    try {
      const actualValues =
        (await actions?.beforeSave?.(valuesWithMeta)) ?? valuesWithMeta;

      const response = await handleLoadingFeedback(
        async () => await saveTicket(actualValues),
        t("maintenance:ticket.create.saving"),
        t("maintenance:ticket.create.saved"),
        t("maintenance:ticket.create.error")
      );
      await actions?.onSaveSuccess?.(response, actualValues);
      return response;
    } catch (e) {
      await actions?.onSaveError?.(e);
    }
  }
  return { onSave };
};
